/**
 * ScansioneConsultazione — modalità CONSULTAZIONE ARTICOLO via pistola barcode/QR.
 *
 * Differenza da ScansioneInventario:
 *   - QUI si LEGGE soltanto (no movimenti carico/scarico/conteggio)
 *   - Su ogni scan mostra la SCHEDA articolo grande e leggibile:
 *       · NOME MATERIALE come informazione primaria
 *       · barcode/codice articolo come secondaria
 *       · categoria, giacenza, minima, prezzo, ubicazione, foto
 *       · badge sotto-scorta
 *   - Mantiene lo storico delle ultime scansioni (click per ri-visualizzare)
 *
 * Compatibile con qualsiasi lettore HID (USB / Bluetooth) che si comporta come
 * tastiera: digita il codice + Enter. Input a focus persistente.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ScanLine, X, Package, AlertTriangle, MapPin, Tag, Boxes, Euro,
  RefreshCw, CheckCircle2, XCircle, Clock, Ruler, Building2, ImageOff,
} from "lucide-react";
import api from "@/api";

// Log della scansione sul server (non blocca UX se fallisce)
async function logScan(payload) {
  try {
    await api.post("/magazzino/scansioni/log", { contesto: "consultazione", ...payload });
  } catch { /* silent */ }
}

const eur = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const _now = () => new Date().toISOString();
const _fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

// Beep sintetico riutilizzato dalla ScansioneInventario (Web Audio API)
let _audioCtx = null;
function beep(freq = 880, ms = 90, gain = 0.15) {
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const osc = _audioCtx.createOscillator();
    const g = _audioCtx.createGain();
    osc.frequency.value = freq;
    osc.type = "sine";
    g.gain.value = gain;
    osc.connect(g).connect(_audioCtx.destination);
    osc.start();
    setTimeout(() => { osc.stop(); }, ms);
  } catch { /* silent */ }
}
const beepOk = () => beep(1200, 70);
const beepErr = () => beep(220, 200);

const CATEGORIE_LABEL = {
  edile: "Edile",
  elettrico: "Elettrico",
  idraulico: "Idraulico",
  generico: "Generico",
};
const CATEGORIE_COLOR = {
  edile: "bg-blue-100 text-blue-800 border-blue-300",
  elettrico: "bg-yellow-100 text-yellow-800 border-yellow-300",
  idraulico: "bg-cyan-100 text-cyan-800 border-cyan-300",
  generico: "bg-slate-100 text-slate-700 border-slate-300",
};

export default function ScansioneConsultazione({ onClose, canSeePrices = true }) {
  const [barcodeBuf, setBarcodeBuf] = useState("");
  const [current, setCurrent] = useState(null);    // scheda mostrata (materiale o notFound o ubicazione)
  const [history, setHistory] = useState([]);      // ultime 8 scansioni
  const [lastScan, setLastScan] = useState(null);  // debounce
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Focus persistente sull'input nascosto
  useEffect(() => {
    const focus = () => inputRef.current && inputRef.current.focus();
    focus();
    const onBlur = () => setTimeout(() => {
      if (document.activeElement?.closest?.("[data-scan-safe]")) return;
      focus();
    }, 50);
    const onClick = (e) => {
      if (e.target.closest("[data-scan-safe]")) return;
      focus();
    };
    inputRef.current && inputRef.current.addEventListener("blur", onBlur);
    document.addEventListener("click", onClick);
    return () => {
      inputRef.current && inputRef.current.removeEventListener("blur", onBlur);
      document.removeEventListener("click", onClick);
    };
  }, []);

  const handleScan = async (rawCode) => {
    const code = (rawCode || "").trim();
    if (!code) return;
    // Debounce anti-doppia scansione 400ms
    if (lastScan && lastScan.code === code && Date.now() - lastScan.ts < 400) {
      setBarcodeBuf("");
      return;
    }
    setLastScan({ code, ts: Date.now() });
    setBarcodeBuf("");
    setLoading(true);
    // Detezione QR ubicazione (payload "UBIC:<nome>") — genera dai QR scaffale
    if (/^UBIC:/i.test(code)) {
      const ubic = code.slice(5).trim();
      try {
        const { data } = await api.get(
          `/magazzino/materiali/by-ubicazione?ubicazione=${encodeURIComponent(ubic)}`
        );
        const scheda = {
          kind: "ubicazione",
          code_scan: code,
          ubicazione: data.ubicazione,
          materiali: data.materiali || [],
          scanned_at: _now(),
        };
        setCurrent(scheda);
        setHistory((prev) => [scheda, ...prev.filter((h) => h.ubicazione !== ubic || h.kind !== "ubicazione")].slice(0, 8));
        beepOk();
        logScan({ code_scan: code, found: true, kind: "ubicazione", ubicazione: ubic });
      } catch (e) {
        beepErr();
        const scheda = {
          kind: "error",
          code_scan: code,
          scanned_at: _now(),
          error: e?.response?.data?.detail || "Ubicazione non trovata",
        };
        setCurrent(scheda);
        setHistory((prev) => [scheda, ...prev].slice(0, 8));
        logScan({ code_scan: code, found: false, kind: "ubicazione", ubicazione: ubic });
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      const { data: mat } = await api.get(
        `/magazzino/materiali/by-barcode/${encodeURIComponent(code)}`
      );
      const scheda = {
        kind: "materiale",
        found: true,
        code_scan: code,
        materiale_id: mat.materiale_id,
        nome: mat.nome,
        codice: mat.codice || "",
        barcode: mat.barcode || code,
        categoria: mat.categoria || "generico",
        unita_misura: mat.unita_misura || "pz",
        quantita_disponibile: Number(mat.quantita_disponibile || 0),
        quantita_minima: Number(mat.quantita_minima || 0),
        prezzo_unitario: mat.prezzo_unitario != null ? Number(mat.prezzo_unitario) : null,
        fornitore: mat.fornitore || "",
        ubicazione: mat.ubicazione || "",
        note: mat.note || "",
        foto: mat.foto_base64 || null,
        scanned_at: _now(),
      };
      setCurrent(scheda);
      setHistory((prev) => [scheda, ...prev.filter((h) => h.materiale_id !== mat.materiale_id)].slice(0, 8));
      beepOk();
      logScan({ code_scan: code, found: true, kind: "materiale", materiale_id: mat.materiale_id, materiale_nome: mat.nome });
    } catch (e) {
      const notFound = e?.response?.status === 404;
      const scheda = {
        kind: "materiale",
        found: false,
        code_scan: code,
        scanned_at: _now(),
        error: notFound
          ? "Nessun materiale associato a questo codice."
          : (e?.response?.data?.detail || e.message || "Errore lookup"),
      };
      setCurrent(scheda);
      setHistory((prev) => [scheda, ...prev].slice(0, 8));
      beepErr();
      if (!notFound) toast.error(scheda.error);
      logScan({ code_scan: code, found: false, kind: "materiale" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/95 backdrop-blur-sm flex flex-col"
      data-testid="scansione-consultazione"
    >
      {/* Header */}
      <div className="bg-slate-900 text-white p-4 flex items-center gap-3 border-b border-slate-700" data-scan-safe>
        <ScanLine className="w-7 h-7 text-cyan-400" />
        <div className="flex-1">
          <div className="overline text-cyan-400">Consultazione articolo</div>
          <h2 className="font-display font-black text-xl md:text-2xl">
            Scansione barcode / QR · Ricerca in Magazzino
          </h2>
        </div>
        <button
          onClick={onClose}
          data-testid="btn-chiudi-consultazione"
          className="p-2 hover:bg-slate-800 rounded-sm"
          title="Chiudi"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Prompt scansione */}
      <div className="bg-cyan-500/10 border-b border-cyan-500/30 p-3 text-center" data-scan-safe>
        <div className="text-cyan-300 text-xs md:text-sm font-bold uppercase tracking-widest flex items-center justify-center gap-2">
          {loading ? (
            <>
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              Ricerca in corso…
            </>
          ) : (
            <>
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              In attesa di scansione…
            </>
          )}
        </div>
        <div className="text-white/70 text-[11px] md:text-xs mt-0.5">
          Punta il lettore su un codice a barre / QR — la scheda articolo comparirà qui sotto (Invio conferma)
        </div>
        <input
          ref={inputRef}
          value={barcodeBuf}
          onChange={(e) => setBarcodeBuf(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleScan(barcodeBuf);
            }
          }}
          data-testid="consulta-barcode-input"
          autoComplete="off"
          autoCapitalize="off"
          data-no-uppercase
          className="absolute opacity-0 pointer-events-none"
          aria-label="Campo scansione barcode"
        />
      </div>

      {/* Corpo: scheda articolo + storico */}
      <div className="flex-1 overflow-y-auto p-3 md:p-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Scheda principale */}
          <div className="lg:col-span-2" data-scan-safe>
            {!current ? (
              <EmptyState />
            ) : current.kind === "ubicazione" ? (
              <UbicazioneScheda scheda={current} canSeePrices={canSeePrices} />
            ) : current.found ? (
              <SchedaArticolo scheda={current} canSeePrices={canSeePrices} />
            ) : (
              <NotFoundCard
                scheda={current}
                onAssociated={(mat, code) => {
                  const found = {
                    kind: "materiale",
                    found: true,
                    code_scan: code,
                    materiale_id: mat.materiale_id,
                    nome: mat.nome,
                    codice: mat.codice || "",
                    barcode: mat.barcode || code,
                    categoria: mat.categoria || "generico",
                    unita_misura: mat.unita_misura || "pz",
                    quantita_disponibile: Number(mat.quantita_disponibile || 0),
                    quantita_minima: Number(mat.quantita_minima || 0),
                    prezzo_unitario: mat.prezzo_unitario != null ? Number(mat.prezzo_unitario) : null,
                    fornitore: mat.fornitore || "",
                    ubicazione: mat.ubicazione || "",
                    note: mat.note || "",
                    foto: mat.foto_base64 || null,
                    scanned_at: _now(),
                  };

                  setCurrent(found);
                  setHistory((prev) => [
                    found,
                    ...prev.filter((h) => h.materiale_id !== mat.materiale_id),
                  ].slice(0, 8));

                  beepOk();
                  toast.success(`Barcode associato a ${mat.nome}`);
                }}
              />
            )}
          </div>

          {/* Storico ultime scansioni */}
          <div data-scan-safe>
            <div className="bg-white/95 rounded-md shadow-lg border border-slate-200 overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-900 text-white">
                <div className="overline text-slate-400">Ultime scansioni</div>
                <div className="font-display font-bold text-sm">Storico sessione</div>
              </div>
              {history.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-xs">
                  Nessuna scansione ancora in questa sessione.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto">
                  {history.map((h, i) => (
                    <li
                      key={`${h.code_scan}-${i}`}
                      onClick={() => (h.kind === "ubicazione" || h.found) && setCurrent(h)}
                      data-testid={`storico-${i}`}
                      className={`p-3 flex items-center gap-2 text-xs hover:bg-slate-50 ${
                        (h.kind === "ubicazione" || h.found) ? "cursor-pointer" : "cursor-default"
                      }`}
                    >
                      {h.kind === "ubicazione" ? (
                        <MapPin className="w-4 h-4 text-cyan-600 shrink-0" />
                      ) : h.found ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-rose-600 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-900 truncate">
                          {h.kind === "ubicazione"
                            ? `📍 ${h.ubicazione}`
                            : h.found
                            ? h.nome
                            : "Codice non trovato"}
                        </div>
                        <div className="font-mono text-[10px] text-slate-500 truncate">
                          {h.code_scan}
                        </div>
                      </div>
                      <div className="text-[10px] text-slate-400 whitespace-nowrap flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {_fmtTime(h.scanned_at)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {  return (
    <div className="bg-white/95 rounded-md shadow-lg border border-slate-200 p-10 text-center">
      <div className="w-24 h-24 mx-auto rounded-full bg-cyan-100 flex items-center justify-center mb-4">
        <ScanLine className="w-12 h-12 text-cyan-600" />
      </div>
      <div className="font-display font-black text-2xl text-slate-900 mb-2">
        In attesa di scansione…
      </div>
      <div className="text-slate-500 text-sm max-w-md mx-auto">
        Scansiona un codice a barre / QR con la pistola: il materiale associato in Magazzino verrà
        mostrato qui in dettaglio.
      </div>
      <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 border border-slate-200 rounded-full text-[11px] text-slate-500 font-mono">
        Es. <b className="text-slate-800">8051234</b> · Invio conferma la lettura
      </div>
    </div>
  );
}

function NotFoundCard({ scheda, onAssociated }) {
  const [search, setSearch] = useState("");
  const [candidati, setCandidati] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [nuovoNome, setNuovoNome] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const q = search.trim();

    if (q.length < 2) {
      setCandidati([]);
      return;
    }

    setLoadingSearch(true);

    const t = setTimeout(() => {
      api.get(`/magazzino/materiali/simili?q=${encodeURIComponent(q)}&limit=20`)
        .then((r) => setCandidati(r.data || []))
        .catch(() => setCandidati([]))
        .finally(() => setLoadingSearch(false));
    }, 250);

    return () => clearTimeout(t);
  }, [search]);

  const associa = async (mat) => {
    setSavingId(mat.materiale_id);

    try {
      const { data } = await api.post(
        `/magazzino/materiali/${mat.materiale_id}/associa-barcode`,
        { barcode: scheda.code_scan }
      );

      onAssociated?.(data, scheda.code_scan);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
        "Errore durante l'associazione del barcode"
      );
    } finally {
      setSavingId(null);
    }
  };


  const creaNuovo = async () => {
    const nome = nuovoNome.trim();

    if (!nome) {
      toast.error("Inserisci il nome del materiale");
      return;
    }

    setCreating(true);

    try {
      const payload = {
        nome,
        barcode: scheda.code_scan,
        categoria: "generico",
        unita_misura: "pz",
        quantita_disponibile: 0,
        prezzo_unitario: 0,
      };

      const { data } = await api.post("/magazzino/materiali", payload);

      toast.success(`Materiale creato: ${data.nome}`);
      onAssociated?.(data, scheda.code_scan);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
        "Errore durante la creazione del materiale"
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="bg-white/95 rounded-md shadow-lg border-2 border-rose-300 overflow-hidden"
      data-testid="scheda-not-found"
      data-scan-safe
    >
      <div className="bg-rose-600 text-white p-4 flex items-center gap-3">
        <AlertTriangle className="w-8 h-8" />
        <div>
          <div className="overline text-rose-100">Nessun articolo</div>
          <div className="font-display font-black text-xl">
            Codice non associato
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="text-center mb-5">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">
            Barcode letto
          </div>

          <div className="font-mono text-2xl font-black text-slate-900 mb-2">
            {scheda.code_scan}
          </div>

          <div className="text-sm text-slate-600">
            {scheda.error}
          </div>
        </div>

        <div className="border-t border-slate-200 pt-4">
          <div className="text-sm font-bold text-slate-900 mb-1">
            Associa a materiale esistente
          </div>

          <div className="text-xs text-slate-500 mb-3">
            Cerca il materiale e associa direttamente il barcode appena letto.
          </div>

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome, codice o categoria..."
            data-no-uppercase
            autoCapitalize="off"
            autoComplete="off"
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
          />

          {loadingSearch && (
            <div className="text-xs text-slate-400 py-3">
              Ricerca in corso...
            </div>
          )}

          {!loadingSearch && search.trim().length >= 2 && candidati.length === 0 && (
            <div className="text-xs text-slate-400 py-3">
              Nessun materiale trovato.
            </div>
          )}

          {candidati.length > 0 && (
            <div className="mt-3 border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {candidati.map((m) => (
                <div
                  key={m.materiale_id}
                  className="p-3 flex items-center gap-3 hover:bg-slate-50"
                >
                  <div className="flex-1 min-w-0 text-left">
                    <div className="font-bold text-sm text-slate-900 truncate">
                      {m.nome}
                    </div>

                    <div className="text-[11px] text-slate-500 font-mono truncate">
                      {m.codice || "—"}
                      {m.barcode ? ` · BC ${m.barcode}` : ""}
                      {m.categoria ? ` · ${m.categoria}` : ""}
                    </div>
                  </div>

                  <button
                    type="button"
                    disabled={!!savingId}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      associa(m);
                    }}
                    className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-sm disabled:opacity-50"
                  >
                    {savingId === m.materiale_id ? "Salvataggio..." : "Associa"}
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 pt-4 border-t-2 border-slate-200">
            <div className="text-sm font-bold text-slate-900 mb-1">
              Oppure crea un nuovo materiale
            </div>

            <div className="text-xs text-slate-500 mb-3">
              Inserisci il nome: il barcode letto verrà salvato automaticamente.
            </div>

            <div className="px-3 py-2 mb-3 bg-blue-50 border border-blue-200 text-xs text-blue-900 rounded-sm">
              Barcode: <b className="font-mono">{scheda.code_scan}</b>
            </div>

            <div className="flex gap-2">
              <input
                value={nuovoNome}
                onChange={(e) => setNuovoNome(e.target.value)}
                placeholder="Nome materiale"
                data-no-uppercase
                autoCapitalize="off"
                className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm"
              />

              <button
                type="button"
                disabled={creating || !nuovoNome.trim()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  creaNuovo();
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-sm disabled:opacity-40"
              >
                {creating ? "Salvataggio..." : "CREA E SALVA BARCODE"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SchedaArticolo({ scheda, canSeePrices }) {
  const sottoScorta = scheda.quantita_disponibile < scheda.quantita_minima;
  const catCls = CATEGORIE_COLOR[scheda.categoria] || CATEGORIE_COLOR.generico;

  return (
    <div
      className="bg-white rounded-md shadow-2xl border border-slate-200 overflow-hidden"
      data-testid={`scheda-articolo-${scheda.materiale_id}`}
    >
      {/* Header: NOME grande + barcode secondario */}
      <div className={`p-5 md:p-7 ${sottoScorta ? "bg-rose-50 border-b-2 border-rose-300" : "bg-slate-900 text-white"}`}>
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className={`overline ${sottoScorta ? "text-rose-700" : "text-cyan-300"} flex items-center gap-2`}>
              <ScanLine className="w-3 h-3" /> Articolo riconosciuto
              {sottoScorta && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-rose-600 text-white text-[10px] font-black uppercase rounded-sm">
                  <AlertTriangle className="w-3 h-3" /> Sotto scorta
                </span>
              )}
            </div>
            {/* NOME COME INFORMAZIONE PRIMARIA */}
            <h1
              data-testid="scheda-nome"
              className={`font-display font-black text-3xl md:text-5xl leading-tight break-words mt-1 ${
                sottoScorta ? "text-rose-900" : "text-white"
              }`}
            >
              {scheda.nome}
            </h1>
            {/* Barcode/codice articolo come informazione secondaria */}
            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs md:text-sm">
              <div>
                <div className={`text-[10px] uppercase tracking-widest ${sottoScorta ? "text-rose-500" : "text-slate-400"}`}>
                  Barcode
                </div>
                <div
                  data-testid="scheda-barcode"
                  className={`font-mono font-bold text-base md:text-lg ${sottoScorta ? "text-rose-900" : "text-cyan-300"}`}
                >
                  {scheda.barcode || "—"}
                </div>
              </div>
              {scheda.codice && scheda.codice !== scheda.barcode && (
                <div>
                  <div className={`text-[10px] uppercase tracking-widest ${sottoScorta ? "text-rose-500" : "text-slate-400"}`}>
                    Codice articolo
                  </div>
                  <div className={`font-mono font-bold text-base md:text-lg ${sottoScorta ? "text-rose-900" : "text-white"}`}>
                    {scheda.codice}
                  </div>
                </div>
              )}
              <div>
                <div className={`text-[10px] uppercase tracking-widest ${sottoScorta ? "text-rose-500" : "text-slate-400"}`}>
                  Categoria
                </div>
                <span
                  data-testid="scheda-categoria"
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase border rounded-sm ${catCls}`}
                >
                  <Tag className="w-3 h-3" />
                  {CATEGORIE_LABEL[scheda.categoria] || scheda.categoria}
                </span>
              </div>
            </div>
          </div>

          {/* Foto */}
          <div className="w-28 h-28 md:w-40 md:h-40 rounded-md overflow-hidden bg-slate-100 shrink-0 border-2 border-white shadow-xl">
            {scheda.foto ? (
              <img src={scheda.foto} alt={scheda.nome} className="w-full h-full object-cover" data-testid="scheda-foto" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400">
                <ImageOff className="w-10 h-10" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Corpo dati */}
      <div className="p-5 md:p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <DataCell
          label="Giacenza"
          icon={Boxes}
          value={
            <span data-testid="scheda-qta-disp">
              <b className={`text-3xl font-black ${sottoScorta ? "text-rose-600" : "text-emerald-600"}`}>
                {scheda.quantita_disponibile}
              </b>
              <span className="text-slate-500 text-sm ml-1">{scheda.unita_misura}</span>
            </span>
          }
        />
        <DataCell
          label="Quantità minima"
          icon={AlertTriangle}
          value={
            <span data-testid="scheda-qta-min">
              <b className="text-2xl font-black text-slate-900">{scheda.quantita_minima}</b>
              <span className="text-slate-500 text-sm ml-1">{scheda.unita_misura}</span>
            </span>
          }
        />
        {canSeePrices && scheda.prezzo_unitario != null && (
          <DataCell
            label="Prezzo unitario"
            icon={Euro}
            value={
              <span data-testid="scheda-prezzo">
                <b className="text-2xl font-black text-slate-900">{eur(scheda.prezzo_unitario)}</b>
                <span className="text-slate-500 text-sm">/{scheda.unita_misura}</span>
              </span>
            }
          />
        )}
        <DataCell
          label="Ubicazione"
          icon={MapPin}
          value={
            <span data-testid="scheda-ubicazione" className="font-bold text-lg text-slate-900">
              {scheda.ubicazione || <span className="text-slate-400 font-normal italic text-sm">Non specificata</span>}
            </span>
          }
        />
        <DataCell
          label="Unità di misura"
          icon={Ruler}
          value={<span className="font-bold text-lg text-slate-900">{scheda.unita_misura}</span>}
        />
        {scheda.fornitore && (
          <DataCell
            label="Fornitore"
            icon={Building2}
            value={<span className="font-bold text-lg text-slate-900 truncate block">{scheda.fornitore}</span>}
          />
        )}
        {canSeePrices && scheda.prezzo_unitario != null && (
          <DataCell
            label="Valore totale"
            icon={Euro}
            value={
              <span className="font-bold text-lg text-slate-900">
                {eur((scheda.quantita_disponibile || 0) * (scheda.prezzo_unitario || 0))}
              </span>
            }
          />
        )}
      </div>

      {scheda.note && (
        <div className="px-5 md:px-6 pb-5">
          <div className="overline text-slate-500 mb-1">Note</div>
          <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-sm p-3 whitespace-pre-wrap">
            {scheda.note}
          </div>
        </div>
      )}

      <div className="px-5 md:px-6 py-2 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-500 flex items-center justify-between">
        <span>Scansionato alle <b>{_fmtTime(scheda.scanned_at)}</b></span>
        <span className="font-mono">ID: {scheda.materiale_id}</span>
      </div>
    </div>
  );
}

function DataCell({ label, icon: Icon, value }) {
  return (
    <div className="border border-slate-200 rounded-sm p-3 bg-white">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1 mb-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div>{value}</div>
    </div>
  );
}

function UbicazioneScheda({ scheda, canSeePrices }) {
  const items = scheda.materiali || [];
  const sottoScorta = items.filter(
    (m) => Number(m.quantita_disponibile || 0) < Number(m.quantita_minima || 0)
  );
  return (
    <div
      className="bg-white rounded-md shadow-2xl border border-slate-200 overflow-hidden"
      data-testid="scheda-ubicazione"
    >
      <div className="bg-cyan-700 text-white p-5 md:p-7">
        <div className="overline text-cyan-200 flex items-center gap-2">
          <ScanLine className="w-3 h-3" /> Etichetta scaffale scansionata
        </div>
        <div className="mt-1 flex items-start gap-3">
          <MapPin className="w-12 h-12 md:w-16 md:h-16 text-cyan-200 shrink-0" />
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-black text-3xl md:text-5xl leading-tight break-words">
              {scheda.ubicazione}
            </h1>
            <div className="mt-2 text-cyan-100 text-sm">
              <b>{items.length}</b> material{items.length === 1 ? "e" : "i"} stoccati in questa ubicazione
              {sottoScorta.length > 0 && (
                <span className="ml-3 inline-flex items-center gap-1 px-2 py-0.5 bg-rose-600 text-white text-[10px] font-black uppercase rounded-sm">
                  <AlertTriangle className="w-3 h-3" /> {sottoScorta.length} sotto scorta
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="max-h-[520px] overflow-y-auto">
        {items.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            Nessun materiale con questa ubicazione.<br />
            <span className="text-[11px] text-slate-400">
              Assegna il campo "Ubicazione" ai materiali del Magazzino.
            </span>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-widest sticky top-0">
              <tr>
                <th className="text-left p-3">Materiale</th>
                <th className="text-left p-3">Barcode</th>
                <th className="text-right p-3">Giacenza</th>
                <th className="text-right p-3">Minima</th>
                {canSeePrices && <th className="text-right p-3">Prezzo</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((m) => {
                const ss = Number(m.quantita_disponibile || 0) < Number(m.quantita_minima || 0);
                return (
                  <tr
                    key={m.materiale_id}
                    className={`border-t border-slate-100 ${ss ? "bg-rose-50" : "hover:bg-slate-50"}`}
                    data-testid={`ubic-mat-${m.materiale_id}`}
                  >
                    <td className="p-3">
                      <div className="font-bold text-slate-900">{m.nome}</div>
                      {ss && (
                        <div className="text-[10px] text-rose-700 font-bold uppercase mt-0.5 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Sotto scorta
                        </div>
                      )}
                    </td>
                    <td className="p-3 font-mono text-[11px] text-slate-500">
                      {m.barcode || m.codice || "—"}
                    </td>
                    <td className={`p-3 text-right font-mono font-bold ${ss ? "text-rose-700" : "text-emerald-700"}`}>
                      {m.quantita_disponibile} {m.unita_misura}
                    </td>
                    <td className="p-3 text-right font-mono text-slate-600">{m.quantita_minima}</td>
                    {canSeePrices && (
                      <td className="p-3 text-right font-mono">
                        {m.prezzo_unitario != null
                          ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(m.prezzo_unitario)
                          : "—"}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div className="px-5 py-2 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-500 flex items-center justify-between">
        <span>Scansionato alle <b>{_fmtTime(scheda.scanned_at)}</b></span>
        <span className="font-mono">{scheda.code_scan}</span>
      </div>
    </div>
  );
}
